// Service pour gérer les instances de modules via l'API

import type { ModuleInstance, CreateModuleInstanceDto, UpdateModuleInstanceDto } from './types';

export class ModuleInstancesApiService {
    private baseUrl: string;
    private readToken: string;
    private writeToken: string;

    constructor(baseUrl: string, readToken: string, writeToken: string) {
        this.baseUrl = baseUrl;
        this.readToken = readToken;
        this.writeToken = writeToken;
    }

    /**
     * Récupère toutes les instances (optionnellement filtrées par page)
     */
    async getAllInstances(page?: string): Promise<ModuleInstance[]> {
        try {
            const url = page 
                ? `${this.baseUrl}/api/v1/instances?page=${encodeURIComponent(page)}`
                : `${this.baseUrl}/api/v1/instances`;

            console.log(`📡 GET ${url}`);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.readToken}`
                }
            });

            console.log(`📡 Response status: ${response.status}`);

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                console.log('📡 Error data:', errorData);
                
                // Si l'erreur est "Module non trouvé", c'est juste qu'il n'y a pas d'instances
                // Retourner une liste vide au lieu de lancer une erreur
                if (errorData?.detail === "Module non trouvé" || response.status === 404) {
                    console.warn('⚠️ Aucune instance trouvée pour cette page, retour d\'une liste vide');
                    return [];
                }
                
                throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            console.log(`✅ ${data.length} instance(s) récupérée(s)`);
            return data;
        } catch (error) {
            // Si l'erreur contient "Module non trouvé", retourner une liste vide
            if (error instanceof Error && error.message.includes('Module non trouvé')) {
                console.warn('⚠️ Aucune instance trouvée, retour d\'une liste vide');
                return [];
            }
            
            this.logError('GET /api/v1/instances', error);
            throw error;
        }
    }

    /**
     * Récupère une instance spécifique par son ID
     */
    async getInstanceById(instanceId: string): Promise<ModuleInstance> {
        try {
            const response = await fetch(`${this.baseUrl}/api/v1/instances/${instanceId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.readToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            this.logError(`GET /api/v1/instances/${instanceId}`, error);
            throw error;
        }
    }

    /**
     * Crée une nouvelle instance
     */
    async createInstance(instance: CreateModuleInstanceDto): Promise<ModuleInstance> {
        try {
            const response = await fetch(`${this.baseUrl}/api/v1/instances`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.writeToken}`
                },
                body: JSON.stringify(instance)
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            this.logError('POST /api/v1/instances', error);
            throw error;
        }
    }

    /**
     * Met à jour une instance existante
     */
    async updateInstance(instanceId: string, updates: UpdateModuleInstanceDto): Promise<ModuleInstance> {
        try {
            const response = await fetch(`${this.baseUrl}/api/v1/instances/${instanceId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.writeToken}`
                },
                body: JSON.stringify(updates)
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            this.logError(`PUT /api/v1/instances/${instanceId}`, error);
            throw error;
        }
    }

    /**
     * Supprime une instance
     */
    async deleteInstance(instanceId: string): Promise<string> {
        try {
            const response = await fetch(`${this.baseUrl}/api/v1/instances/${instanceId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.writeToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            this.logError(`DELETE /api/v1/instances/${instanceId}`, error);
            throw error;
        }
    }

    /**
     * Logs une erreur
     */
    private logError(endpoint: string, error: unknown): void {
        const errorMessage = {
            timestamp: new Date().toISOString(),
            endpoint,
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined
        };

        console.error('❌ Erreur API Instances:', errorMessage);
        this.saveErrorToLocalStorage(errorMessage);
    }

    /**
     * Sauvegarde les erreurs dans le localStorage
     */
    private saveErrorToLocalStorage(error: any): void {
        try {
            const errors = JSON.parse(localStorage.getItem('gandc_api_errors') || '[]');
            errors.push(error);
            
            if (errors.length > 100) {
                errors.shift();
            }
            
            localStorage.setItem('gandc_api_errors', JSON.stringify(errors));
        } catch (e) {
            console.error('Impossible de sauvegarder les erreurs:', e);
        }
    }
}

